import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Laptop, Home, Wrench, Truck, Zap, ArrowRight, Code, Lightbulb, Plane } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Services = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const icons = [Briefcase, Laptop, Code, Home, Wrench, Truck, Zap, Lightbulb, Plane];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-blue-900/85"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t.services.title}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gradient-to-br from-white via-blue-50/30 to-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.services.categories.map((service, index) => {
              const Icon = icons[index];
              return (
                <Card
                  key={index}
                  className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-500 group bg-white"
                >
                  {/* Service Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Service Content */}
                  <div className="p-6 space-y-4">
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="pt-4 border-t border-gray-200">
                      <ul className="space-y-3">
                        {service.items.map((item, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-700">
                            <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></div>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Links to detailed pages */}
                    <div className="pt-4">
                      <Link to={
                        index === 0 ? '/services/professional-equipment' :
                        index === 1 ? '/services/it-electronics' :
                        index === 2 ? '/services/digital-solutions' :
                        index === 3 ? '/services/appliances' :
                        index === 4 ? '/services/maintenance' :
                        index === 5 ? '/services/vehicles-logistics' :
                        index === 6 ? '/services/energy-solutions' :
                        index === 7 ? '/services/electrical-infrastructure' :
                        '/services/aeronautical'
                      }>
                        <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                          {language === 'fr' ? 'Voir les détails' : 'View details'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visual Gallery Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Notre Excellence en Images' : 'Our Excellence in Images'}
            </h2>
            <p className="text-lg text-gray-600">
              {language === 'fr' 
                ? 'Découvrez nos réalisations et nos domaines d\'expertise'
                : 'Discover our achievements and areas of expertise'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative group overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <img 
                src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjB0ZWFtd29ya3xlbnwwfHx8fDE3NjQ0NTQ0MzV8MA&ixlib=rb-4.1.0&q=85"
                alt="Team Work"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end">
                <p className="text-white font-semibold p-4">
                  {language === 'fr' ? 'Équipe Professionnelle' : 'Professional Team'}
                </p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <img 
                src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZXF1aXBtZW50fGVufDB8fHx8MTc2NDQ1NDQ0MXww&ixlib=rb-4.1.0&q=85"
                alt="Industrial Equipment"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end">
                <p className="text-white font-semibold p-4">
                  {language === 'fr' ? 'Équipements Industriels' : 'Industrial Equipment'}
                </p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <img 
                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3NjQ0NTQ0NDd8MA&ixlib=rb-4.1.0&q=85"
                alt="Solar Energy"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end">
                <p className="text-white font-semibold p-4">
                  {language === 'fr' ? 'Solutions Énergétiques' : 'Energy Solutions'}
                </p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <img 
                src="https://images.unsplash.com/photo-1629019896873-cc95f1a8845d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjB2ZWhpY2xlc3xlbnwwfHx8fDE3NjQ0NTQ0NTN8MA&ixlib=rb-4.1.0&q=85"
                alt="Logistics"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end">
                <p className="text-white font-semibold p-4">
                  {language === 'fr' ? 'Logistique & Transport' : 'Logistics & Transport'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              {language === 'fr' ? 'Pourquoi choisir PRESTACO ?' : 'Why choose PRESTACO?'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4 p-6 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex-shrink-0"></div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {language === 'fr' ? 'Qualité Garantie' : 'Guaranteed Quality'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'fr'
                      ? 'Produits et services de haute qualité certifiés'
                      : 'High-quality certified products and services'}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 p-6 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex-shrink-0"></div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {language === 'fr' ? 'Support 24/7' : '24/7 Support'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'fr'
                      ? 'Équipe disponible pour répondre à vos besoins'
                      : 'Team available to meet your needs'}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 p-6 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex-shrink-0"></div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {language === 'fr' ? 'Solutions Sur Mesure' : 'Custom Solutions'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'fr'
                      ? 'Adaptées à vos besoins spécifiques'
                      : 'Adapted to your specific needs'}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 p-6 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex-shrink-0"></div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {language === 'fr' ? 'Prix Compétitifs' : 'Competitive Prices'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'fr'
                      ? 'Meilleur rapport qualité-prix du marché'
                      : 'Best value for money in the market'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;